import React, { FC, useContext, useState } from 'react';
import { FormInput } from 'Shared/components';
import { Asset, CalendarIcon, DatePicker, Modal } from '@clearcover/atlas';
import styles from './styles.module.css';
import { DateTime } from 'luxon';
import { PolicyContext } from 'Policies/utils';

type DatePickerModalProps = {
  classNames?: string;
  errorMessage?: string;
  disabled?: boolean;
  onDateChange: (date: string) => void;
  label: string;
  showEffectiveTime?: boolean;
  startDate?: Date;
  isOptional?: boolean;
  minDateAllowed?: Date;
  maxDateAllowed?: Date;
  /**
   * Optional override for how the selected JS Date is converted to the string
   * passed to `onDateChange`. When provided, the default policy-timezone ISO
   * formatting is skipped and this function's return value is used instead.
   * The JS Date received from Atlas is always UTC midnight for the selected day.
   */
  dateFormatter?: (date: Date) => string;
};

export const DatePickerModal: FC<DatePickerModalProps> = ({
  classNames,
  onDateChange,
  errorMessage,
  disabled = false,
  label,
  showEffectiveTime = true,
  startDate,
  isOptional,
  minDateAllowed,
  maxDateAllowed,
  dateFormatter,
}) => {
  const {
    details: { isRenewalDraft, effectiveDate: draftEffectiveDate, timeZone },
  } = useContext(PolicyContext);

  const [initalizedDate, setInitializedDate] = useState<DateTime>(
    DateTime.fromISO(draftEffectiveDate, {
      zone: timeZone,
    })
  );

  return (
    <Modal
      variant="normal"
      data-testid="effective-date-modal"
      toggle={open => (
        <div className={classNames}>
          <FormInput
            disabled={isRenewalDraft || disabled}
            readOnly
            variant="text"
            icon={
              <Asset
                className={styles.icon}
                icon={CalendarIcon}
                description="Calendar Icon"
                height={20}
                width={20}
              />
            }
            className={styles.effectiveDateInput}
            name="date"
            maxLength={10}
            onClick={open}
            value={
              startDate
                ? DateTime.fromJSDate(startDate).toLocaleString({ timeZone })
                : isOptional
                ? ''
                : initalizedDate.toLocaleString({ timeZone })
            }
            label={label}
            errorMessage={errorMessage}
          />
          {showEffectiveTime && (
            <div className={styles.effectiveTime}>
              {startDate
                ? DateTime.fromJSDate(startDate)
                    .setZone(timeZone || '')
                    .toFormat('ttt')
                : initalizedDate.setZone(timeZone || '').toFormat('ttt')}
            </div>
          )}
        </div>
      )}
    >
      {close => (
        <div className={styles.datePickerContainer}>
          <div className={styles.datePickerHeader}>
            Select effective date of change
          </div>
          <DatePicker
            data-testid="date-picker"
            className={styles.effectiveDateSelect}
            initializeDate={startDate ?? new Date()}
            minDateAllowed={minDateAllowed}
            maxDateAllowed={maxDateAllowed}
            onSelect={date => {
              if (dateFormatter) {
                onDateChange(dateFormatter(date));
              } else {
                const isoDateWithPolicyTimeZone = DateTime.fromISO(
                  DateTime.fromJSDate(date).toISODate(),
                  { zone: timeZone }
                );
                setInitializedDate(isoDateWithPolicyTimeZone);
                onDateChange(isoDateWithPolicyTimeZone.toISO());
              }
              close();
            }}
          />
        </div>
      )}
    </Modal>
  );
};
